import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { CloudArrowDown, Warehouse } from '@phosphor-icons/react'
import { useNetworkStatus } from '@/api'
import { useFormat, useI18n, type MessageKey } from '@/i18n'
import { env } from '@/lib/env'
import { downloadCaches, storeCacheAge } from '@/offline'
import { Banner, BilingualLabel, Button, EmptyState, Skeleton, useToast } from '@/ui'
import { ScreenTitle } from '@/features/phone/components/ScreenTitle'
import type { StoreState } from '../projection'
import { useNow, useStoreState } from '../useStore'

/** "Update store list" — GET /sites/{id}/store-cache (or the collection point's on a village phone). */
export function CacheUpdateButton({
  variant = 'secondary',
}: {
  variant?: 'secondary' | 'primary'
}) {
  const { t } = useI18n()
  const toast = useToast()
  const { online } = useNetworkStatus()
  const [busy, setBusy] = useState(false)
  return (
    <Button
      variant={variant}
      size="touch"
      leadingIcon={<CloudArrowDown aria-hidden="true" />}
      loading={busy}
      disabled={!online}
      onClick={() => {
        setBusy(true)
        downloadCaches()
          .then(() => toast.success(t('store.cache.updated')))
          .catch((e: unknown) =>
            toast.error(t('store.cache.failed'), e instanceof Error ? e.message : undefined),
          )
          .finally(() => setBusy(false))
      }}
    >
      {online ? t('store.cache.update') : t('store.cache.needsNetwork')}
    </Button>
  )
}

/** Age of the store list with a warning when it is old (F5.3 offline: refreshed at each sync). */
export function CacheBar({ downloadedAt }: { downloadedAt: number }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const now = useNow()
  const age = storeCacheAge(downloadedAt, env.rosterMaxAgeDays, now)
  const when = fmt.relative(downloadedAt)
  if (age === 'fresh')
    return <p className="text-[12px] text-muted">{t('store.cache.age', { when })}</p>
  return (
    <Banner
      tone={age === 'expired' ? 'error' : 'warn'}
      title={t(age === 'expired' ? 'store.cache.expired' : 'store.cache.stale')}
      action={<CacheUpdateButton />}
    >
      {t('store.cache.age', { when })}
    </Banner>
  )
}

export interface StoreScreenProps {
  title: MessageKey
  children: (state: StoreState) => ReactNode
  /** Extra controls on the title row. */
  aside?: ReactNode
}

/** Shell for every store phone screen: back, bilingual title, store-list age, then the flow. */
export function StoreScreen({ title, children, aside }: StoreScreenProps) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { loading, state } = useStoreState()
  return (
    <div className="flex flex-col gap-4">
      <ScreenTitle
        title={title}
        sub={state?.cache.site_name}
        aside={aside}
        onBack={() => navigate(-1)}
      />
      {loading ? (
        <Skeleton className="h-40 w-full" />
      ) : !state ? (
        <EmptyState
          icon={<Warehouse />}
          title={<BilingualLabel k="store.cache.missingTitle" layout="stacked" />}
          description={t('store.cache.missingBody')}
          action={<CacheUpdateButton variant="primary" />}
        />
      ) : (
        <>
          <CacheBar downloadedAt={state.downloadedAt} />
          {children(state)}
        </>
      )}
    </div>
  )
}
